// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Loader, Stack } from '@mantine/core';
import type { Patient } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useState } from 'react';
import { usePatient } from '../../../hooks/usePatient';
import { usePatientChargeItems } from '../../../hooks/usePatientChargeItems';
import { usePatientConsents } from '../../../hooks/usePatientConsents';
import { usePatientPrimaryCoverage } from '../../../hooks/usePatientPrimaryCoverage';
import { usePatientReferringProvider } from '../../../hooks/usePatientReferringProvider';
import { useRole } from '../../../hooks/useRole';
import { hasPermission } from '../../../utils/permissions';
import { ConsentsGrid } from './ConsentsGrid';
import { InsuranceSection } from './InsuranceSection';
import { OutstandingBalanceAlert } from './OutstandingBalanceAlert';
import { PaymentSection } from './PaymentSection';
import { PersonalInformationSection } from './PersonalInformationSection';
import { PharmacySection } from './PharmacySection';
import { ReferralOutSection } from './ReferralOutSection';
import { ReferringProviderSection } from './ReferringProviderSection';

// Slim variant — only demographics + contact for RCM users.
// Avoids calling data hooks that require Coverage/Consent/ChargeItem read rights.
function ProfileTabSlim({ patient }: { patient: Patient }): JSX.Element {
  const [current, setCurrent] = useState<Patient>(patient);

  if (patient !== current && patient.id !== current.id) {
    setCurrent(patient);
  }

  return (
    <Stack gap="lg" p="md">
      <PersonalInformationSection patient={current} onSaved={setCurrent} />
    </Stack>
  );
}

// Full variant — all sections rendered for clinical roles.
function ProfileTabFull({
  canManagePayments,
  canEditProfile,
}: {
  canManagePayments: boolean;
  canEditProfile: boolean;
}): JSX.Element {
  const contextPatient = usePatient();
  const [patient, setPatient] = useState(contextPatient);

  if (contextPatient && contextPatient !== patient && patient?.id !== contextPatient.id) {
    setPatient(contextPatient);
  }

  const active = patient ?? contextPatient;
  const {
    coverage,
    secondaryCoverage,
    payor,
    loading: coverageLoading,
    setCoverage,
  } = usePatientPrimaryCoverage(active);
  const {
    practitioner,
    organization,
    loading: referringLoading,
    setReferringProvider,
  } = usePatientReferringProvider(active);
  const { chargeItems, loading: chargesLoading, refresh: refreshChargeItems } = usePatientChargeItems(active);
  const { consents, loading: consentsLoading, setConsents } = usePatientConsents(active);

  if (!active) {
    return (
      <Stack align="center" p="md">
        <Loader size="sm" />
      </Stack>
    );
  }

  return (
    <Stack gap="lg" p="md">
      <OutstandingBalanceAlert patient={active} />
      <PersonalInformationSection patient={active} onSaved={setPatient} />
      <PharmacySection patient={active} canEdit={canEditProfile} />
      <InsuranceSection
        patient={active}
        coverage={coverage}
        secondaryCoverage={secondaryCoverage}
        payor={payor}
        loading={coverageLoading}
        onSaved={setCoverage}
      />
      <PaymentSection
        patient={active}
        chargeItems={chargeItems}
        loading={chargesLoading}
        canManagePayments={canManagePayments}
        onPatientSaved={setPatient}
        onChargeItemsChanged={refreshChargeItems}
      />
      <ReferringProviderSection
        patient={active}
        practitioner={practitioner}
        organization={organization}
        loading={referringLoading}
        onSaved={(updatedPatient, p, o) => {
          setPatient(updatedPatient);
          setReferringProvider(p, o);
        }}
      />
      <ReferralOutSection patient={active} />
      <ConsentsGrid patient={active} consents={consents} loading={consentsLoading} onConsentsUpdated={setConsents} />
    </Stack>
  );
}

// Thin router — reads role once here; dispatches to the appropriate variant.
// RCM gets the slim variant (avoids FHIR queries for Coverage/Consent/ChargeItem
// that the RCM AccessPolicy does not grant).
export function ProfileTab(): JSX.Element {
  const contextPatient = usePatient();
  const { role, isAdmin } = useRole();

  const isRcm = role === 'clinical-ops-rcm';
  const canManagePayments = hasPermission({ role, isAdmin, permission: 'manage-patient-payments' });
  const canEditProfile = hasPermission({ role, isAdmin, permission: 'edit-patient-profile' });

  if (isRcm) {
    if (!contextPatient) {
      return (
        <Stack align="center" p="md">
          <Loader size="sm" />
        </Stack>
      );
    }
    return <ProfileTabSlim patient={contextPatient} />;
  }

  return <ProfileTabFull canManagePayments={canManagePayments} canEditProfile={canEditProfile} />;
}
